import Header from "units/dashboard/components/Header";
import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import UserSidebar from "../components/UserSidebar";
import { ProgressBar } from "react-loader-spinner";
import { useEffect, useState } from "react";
import {
    FaWallet,
    FaCreditCard,
    FaClock,
    FaCheckCircle,
    FaTimesCircle,
    FaExclamationCircle,
    FaEye,
    FaTimes,
    FaFileImage,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { getknowlageBaseInfo } from "../services/knowlageBaseInfo";
import { userSettlementList, userSettlementSubmit } from "../services/settlementsService";
import axiosInstance from "api/axiosInstance";
import { showToast } from "toast/toastHelper";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Digit helpers
 * ================================================================ */
const persianToEnglishDigits = (str) =>
    String(str).replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));

const englishToPersianDigits = (str) =>
    String(str).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

// 👈 واحد: هر رشته یا عددی رو بگیر، اول لاتینش کن، بعد بر اساس زبان فعلی نشون بده
const toDisplayDigits = (value, isEnglish) => {
    const latin = persianToEnglishDigits(String(value ?? ""));
    return isEnglish ? latin : englishToPersianDigits(latin);
};

const SettlementsInner = () => {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const [loading, setLoading] = useState(false);
    const [settlementType, setSettlementType] = useState("manual");
    const [iban, setIban] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [walletBalance, setWalletBalance] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [requests, setRequests] = useState([]);
    const [loadingRequests, setLoadingRequests] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const accessToken = useSelector((state) => state.auth.accessToken);
    const MIN_SETTLEMENT_AMOUNT = 500000;

    /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024 && isSidebarOpen) toggleSidebar();
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isSidebarOpen, toggleSidebar]);

    const getSettlementRequests = async () => {
        if (!accessToken) return;
        try {
            setLoadingRequests(true);
            const res = await userSettlementList(accessToken);
            const settlements = res?.data || [];
            const mappedRequests = settlements.map((item) => ({
                id: item.id,
                type: item.method,
                amount: item.requestedAmount,
                date: item.createdAt || null, // 👈 تاریخ خام نگه داشته میشه، فرمتش موقع نمایش انجام میشه
                status: item.status,
                iban: item.iban,
                cardNumber: item.cardNumber || null,
                trackingCode: item.trackingCode || "-",
                receiptImageUrl: item.receiptImageUrl || null,
                commissionAmount: item.commissionAmount,
                taxAmount: item.taxAmount,
                netAmount: item.netAmount,
                reviewNote: item.reviewNote,
                reviewedBy: item.reviewedBy,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
                raw: item,
            }));
            setRequests(mappedRequests);
        } catch (error) {
            console.error("خطا در دریافت تاریخچه تسویه:", error);
        } finally {
            setLoadingRequests(false);
        }
    };

    useEffect(() => {
        if (accessToken) getSettlementRequests();
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken) return;
        const getUser = async () => {
            try {
                setLoading(true);
                const res = await getknowlageBaseInfo(accessToken);
                if (res?.ok) {
                    setWalletBalance(res.data.pendingSettlement);
                }
            } catch (error) {
                console.error("خطا در دریافت موجودی کیف پول:", error);
            } finally {
                setLoading(false);
            }
        };
        getUser();
    }, [accessToken]);

    // 👈 فرمت کاما همیشه روی عدد لاتین خام انجام میشه (Intl با en-US)؛ تبدیل به فارسی/عربی فقط موقع نمایش
    const formatRial = (value) => {
        const formatted = new Intl.NumberFormat("en-US").format(Number(value) || 0);
        return toDisplayDigits(formatted, isEnglish);
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return "-";
        const locale = isEnglish ? "en-US" : "fa-IR";
        return new Date(isoDate).toLocaleDateString(locale);
    };

    const parseNumber = (value) => {
        if (!value) return 0;
        return Number(String(value).replace(/,/g, "").replace(/\u066C/g, "").replace(/\u066B/g, ".")) || 0;
    };

    const handleAmountChange = (e) => {
        const value = e.target.value;
        const rawValue = persianToEnglishDigits(value).replace(/[,\u066C]/g, "");
        if (rawValue === "" || /^\d+$/.test(rawValue)) {
            const numValue = Number(rawValue);
            setAmount(rawValue); // 👈 همیشه لاتین خالص ذخیره میشه
            setError("");
            if (numValue > 0) {
                if (numValue > walletBalance) {
                    setError(t("settlements.errors.amountExceedsWallet"));
                } else if (numValue < MIN_SETTLEMENT_AMOUNT) {
                    setError(t("settlements.errors.minAmount"));
                }
            }
        }
    };

    const handleIbanChange = (e) => {
        const value = persianToEnglishDigits(e.target.value).replace(/\D/g, "").slice(0, 24);
        setIban(value);
        setError("");
        setConfirmed(false);
    };

    const handleCardNumberChange = (e) => {
        const value = persianToEnglishDigits(e.target.value).replace(/\D/g, "").slice(0, 16);
        setCardNumber(value);
        setError("");
        setConfirmed(false);
    };

    const handleSettlementTypeChange = (type) => {
        setSettlementType(type);
        setConfirmed(false);
        setError("");
        if (type === "automatic") {
            setCardNumber("");
        }
    };

    const validateForm = () => {
        if (!iban) {
            setError(t("settlements.errors.ibanRequired"));
            return false;
        }
        if (iban.length !== 24) {
            setError(t("settlements.errors.ibanLength"));
            return false;
        }
        if (settlementType === "manual") {
            if (!cardNumber) {
                setError(t("settlements.errors.cardRequired"));
                return false;
            }
            if (cardNumber.length !== 16) {
                setError(t("settlements.errors.cardLength"));
                return false;
            }
        }
        const amountInRial = parseNumber(amount);
        if (!amount || amountInRial === 0) {
            setError(t("settlements.errors.amountRequired"));
            return false;
        }
        if (amountInRial < MIN_SETTLEMENT_AMOUNT) {
            setError(t("settlements.errors.minAmount"));
            return false;
        }
        if (amountInRial > walletBalance) {
            setError(t("settlements.errors.amountExceedsWallet"));
            return false;
        }
        if (!confirmed) {
            setError(t("settlements.errors.confirmRequired"));
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        setError("");
        if (!validateForm()) return;
        const amountInRial = parseNumber(amount);
        try {
            setSubmitting(true);
            const response = await userSettlementSubmit(accessToken, amountInRial, cardNumber, iban);
            if (!response?.ok) {
                throw new Error(response?.message || t("settlements.errors.submitFailed"));
            }
            const settlement = response.data;
            const newRequest = {
                id: settlement.id,
                type: settlement.method,
                amount: settlement.requestedAmount,
                date: settlement.createdAt || null, // 👈 خام
                status: settlement.status,
                iban: settlement.iban,
                cardNumber: settlement.cardNumber || null,
                trackingCode: settlement.trackingCode || "-",
                receiptImageUrl: settlement.receiptImageUrl || null,
            };
            setRequests((prev) => [newRequest, ...prev]);
            setAmount("");
            setIban("");
            setCardNumber("");
            setConfirmed(false);
            showToast(t("settlements.toasts.submitSuccess"), "success");
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error("Settlement error:", error);
            setError(
                error?.response?.data?.message
                || error?.message
                || t("settlements.errors.submitError")
            );
        } finally {
            setSubmitting(false);
        }
    };

    const amountInRial = parseNumber(amount);
    const isAmountValid = amountInRial >= MIN_SETTLEMENT_AMOUNT && amountInRial <= walletBalance;
    const isSettlementInfoValid = iban.length === 24 && (settlementType === "automatic" || cardNumber.length === 16);
    const isFormValid = isAmountValid && isSettlementInfoValid && confirmed;

    const getStatusBadge = (status) => {
        switch (status) {
            case "completed":
            case "approved":
                return {
                    color: "bg-emerald-500/15 text-emerald-700 dark:!text-emerald-300 border border-emerald-500/30",
                    icon: <FaCheckCircle className="w-3.5 h-3.5" />,
                    text: t("settlements.statuses.completed"),
                };
            case "pending":
                return {
                    color: "bg-amber-500/15 text-amber-800 dark:!text-amber-200 border border-amber-500/30",
                    icon: <FaClock className="w-3.5 h-3.5" />,
                    text: t("settlements.statuses.pending"),
                };
            case "rejected":
                return {
                    color: "bg-rose-500/15 text-rose-700 dark:!text-rose-300 border border-rose-500/30",
                    icon: <FaTimesCircle className="w-3.5 h-3.5" />,
                    text: t("settlements.statuses.rejected"),
                };
            default:
                return {
                    color: "bg-slate-500/15 text-slate-700 dark:!text-slate-300 border border-slate-500/30",
                    icon: null,
                    text: t("settlements.statuses.unknown"),
                };
        }
    };

    const getReceiptUrl = (receiptPath) => {
        if (!receiptPath) return "";
        if (receiptPath.startsWith("http://") || receiptPath.startsWith("https://")) {
            return receiptPath;
        }
        const apiBase = axiosInstance?.defaults?.baseURL || "";
        const origin = apiBase.replace(/\/v1\/?$/, "").replace(/\/$/, "");
        const cleanedPath = receiptPath.replace(/^\//, "");
        const normalizedPath = cleanedPath.startsWith("cdn/") ? cleanedPath : `cdn/${cleanedPath}`;
        return `${origin}/${normalizedPath}`;
    };

    const handleShowReceipt = (request) => {
        if (!request.receiptImageUrl) return;
        const receiptUrl = getReceiptUrl(request.receiptImageUrl);
        setSelectedReceipt({ url: receiptUrl, request: request });
    };

    const closeReceiptModal = () => {
        setSelectedReceipt(null);
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:!from-slate-950 dark:!via-slate-900 dark:!to-slate-950">

            {/* ================= Header ================= */}
            <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-slate-100 dark:!border-zinc-700 bg-white/85 dark:!bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] dark:!shadow-[0_4px_30px_-12px_rgba(0,0,0,0.7)] print:hidden">
                <Header />
            </nav>

            {/* ================= Body ================= */}
            <div className="flex-1 min-h-0 relative">

                {isSidebarOpen && (
                    <div
                        onClick={toggleSidebar}
                        className="lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30 bg-slate-900/50 dark:!bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                        aria-hidden="true"
                    />
                )}

                <aside className={`
                    fixed top-[70px] bottom-0 rtl:right-0 ltr:left-0 w-64
                    border-r bg-slate-50 border-gray-50 print:hidden
                    dark:!bg-zinc-800 dark:!border-neutral-700 z-40
                    transform transition-transform duration-300 ease-out
                    ${isSidebarOpen ? "translate-x-0" : "rtl:translate-x-full ltr:-translate-x-full"}
                    lg:!translate-x-0
                    overflow-hidden
                `}>
                    <UserSidebar />
                </aside>

                <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">

                        {loading ? (
                            <div className="flex justify-center items-center min-h-[70vh]">
                                <ProgressBar
                                    visible={true}
                                    height="80"
                                    width="80"
                                    barColor="#14B8A6"
                                    borderColor="#FCD34D"
                                    ariaLabel="progress-bar-loading"
                                />
                            </div>
                        ) : (
                            <div className="p-3 sm:p-4 md:p-6">
                                <div className="mb-5 sm:mb-8">
                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:!text-white">
                                        {t("settlements.pageTitle")}
                                    </h1>
                                    <p className="text-xs sm:text-sm text-gray-600 dark:!text-gray-400 mt-1">
                                        {t("settlements.pageSubtitle")}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                    {/* ================ FORM CARD ================ */}
                                    <div className="bg-white dark:bg-gradient-to-bl dark:!from-zinc-950 dark:!via-slate-900 dark:!to-teal-950 rounded-xl shadow-sm border border-gray-200 dark:!border-zinc-700 p-4 sm:p-6 min-w-0">
                                        <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:!text-white mb-4 sm:mb-6">
                                            {t("settlements.formTitle")}
                                        </h2>

                                        <div className="bg-gradient-to-r from-teal-50 to-teal-100 dark:!from-teal-900/30 dark:!to-teal-800/30 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 border border-teal-200 dark:!border-teal-700">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="flex items-center min-w-0">
                                                    <FaWallet className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 dark:!text-teal-400 ml-2 shrink-0" />
                                                    <span className="text-xs sm:text-sm text-gray-600 dark:!text-gray-300 truncate">
                                                        {t("settlements.walletLabel")}
                                                    </span>
                                                </div>
                                                <div className="flex items-center shrink-0">
                                                    <span className={`text-base sm:text-xl font-bold text-teal-600 dark:!text-teal-400 tabular-nums px-1 ${isEnglish && 'font-sans'}`}>
                                                        {formatRial(walletBalance)}
                                                    </span>
                                                    <span className="mr-1 text-[10px] sm:text-sm text-gray-500 dark:!text-gray-400">
                                                        {t("settlements.currency")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-4 sm:mb-6">
                                            <div className="flex p-1 bg-teal-100 dark:!bg-teal-700 rounded-lg">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSettlementTypeChange("manual")}
                                                    className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-all ${settlementType === "manual"
                                                        ? "bg-white dark:!bg-slate-800 text-teal-600 dark:!text-teal-400 shadow-sm"
                                                        : "text-gray-600 dark:!text-gray-300 hover:text-gray-800 dark:!hover:text-white"
                                                        }`}
                                                >
                                                    {t("settlements.tabs.manual")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`flex-1 py-2 sm:py-2.5 px-2 sm:px-4 rounded-md text-xs sm:text-sm cursor-not-allowed font-medium transition-all ${settlementType === "automatic"
                                                        ? "bg-white dark:!bg-zinc-800 text-teal-600 dark:!text-teal-400 shadow-sm"
                                                        : "text-gray-600 dark:!text-gray-300"
                                                        }`}
                                                >
                                                    {t("settlements.tabs.automatic")}
                                                </button>
                                            </div>
                                            <p className="mt-2 text-[10px] sm:text-xs text-gray-500 dark:!text-gray-400">
                                                {settlementType === "manual"
                                                    ? t("settlements.tabHints.manual")
                                                    : t("settlements.tabHints.automatic")}
                                            </p>
                                        </div>

                                        <div className="mb-4">
                                            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:!text-gray-300 mb-2">
                                                {t("settlements.ibanLabel")}
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 sm:pl-4 pointer-events-none">
                                                    <span className="text-gray-500 dark:!text-gray-300 text-xs sm:text-sm font-semibold" dir="ltr">
                                                        IR
                                                    </span>
                                                </div>
                                                <input
                                                    type="text"
                                                    value={toDisplayDigits(iban, isEnglish)}
                                                    onChange={handleIbanChange}
                                                    placeholder={t("settlements.ibanPlaceholder")}
                                                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pl-10 sm:pl-12 border border-gray-300 dark:!border-zinc-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:!bg-slate-800 dark:!text-white text-left text-sm tabular-nums font-mono placeholder:text-xs sm:placeholder:text-sm"
                                                    dir="ltr"
                                                    maxLength={24}
                                                    inputMode="numeric"
                                                />
                                            </div>
                                            <p className={`mt-1 text-[10px] sm:text-xs text-gray-500 dark:!text-gray-400 ${isEnglish && 'font-sans'}`}>
                                                {t("settlements.ibanHint")}
                                            </p>
                                        </div>

                                        {settlementType === "manual" && (
                                            <div className="mb-4">
                                                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:!text-gray-300 mb-2">
                                                    {t("settlements.cardLabel")}
                                                </label>
                                                <div className="relative">
                                                    <FaCreditCard className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
                                                    <input
                                                        type="text"
                                                        value={toDisplayDigits(cardNumber, isEnglish)}
                                                        onChange={handleCardNumberChange}
                                                        placeholder={t("settlements.cardPlaceholder")}
                                                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-9 sm:pr-10 border border-gray-300 dark:!border-zinc-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:!bg-slate-800 dark:!text-white text-left text-sm tabular-nums font-mono placeholder:text-xs sm:placeholder:text-sm"
                                                        dir="ltr"
                                                        maxLength={16}
                                                        inputMode="numeric"
                                                    />
                                                </div>
                                                <p className={`mt-1 text-[10px] sm:text-xs text-gray-500 dark:!text-gray-400 ${isEnglish && 'font-sans'}`}>
                                                    {t("settlements.cardHint")}
                                                </p>
                                            </div>
                                        )}

                                        <div className="mb-4 sm:mb-6">
                                            <div className="flex items-center justify-between gap-2 mb-2">
                                                <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:!text-gray-300">
                                                    {t("settlements.amountLabel")}
                                                </label>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (walletBalance > 0) {
                                                            setAmount(String(walletBalance));
                                                            setError("");
                                                            if (walletBalance < MIN_SETTLEMENT_AMOUNT) {
                                                                setError(t("settlements.errors.walletBelowMin"));
                                                            }
                                                        }
                                                    }}
                                                    disabled={walletBalance <= 0}
                                                    className={`text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-colors whitespace-nowrap ${walletBalance > 0
                                                        ? "bg-teal-50 text-teal-600 hover:bg-teal-100 dark:!bg-teal-900/30 dark:!text-teal-400 dark:!hover:bg-teal-900/50"
                                                        : "bg-gray-100 text-gray-400 cursor-not-allowed dark:!bg-zinc-700 dark:!text-gray-500"
                                                        }`}
                                                >
                                                    {t("settlements.applyAllBalance")}
                                                </button>
                                            </div>

                                            <input
                                                type="text"
                                                value={
                                                    amount
                                                        ? toDisplayDigits(new Intl.NumberFormat("en-US").format(Number(amount)), isEnglish)
                                                        : ""
                                                }
                                                onChange={handleAmountChange}
                                                placeholder={t("settlements.amountPlaceholder")}
                                                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:!bg-slate-800 dark:!text-white text-left text-sm tabular-nums font-bold placeholder:text-xs sm:placeholder:text-sm ${error
                                                    ? "border-red-500"
                                                    : "border-gray-300 dark:!border-zinc-600"
                                                    }`}
                                                dir="ltr"
                                            />

                                            {error && (
                                                <div className="mt-2 flex items-start gap-1 text-red-500 dark:!text-red-400 text-xs sm:text-sm">
                                                    <FaExclamationCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 mt-0.5" />
                                                    <span>{error}</span>
                                                </div>
                                            )}

                                            {!error && amount && isAmountValid && (
                                                <div className="mt-2 text-emerald-500 dark:!text-emerald-400 text-xs sm:text-sm">
                                                    {t("settlements.amountValid")}
                                                </div>
                                            )}

                                            <div className={`mt-2 text-[10px] sm:text-xs text-gray-500 dark:!text-gray-400 flex flex-wrap gap-1 ${isEnglish && 'font-sans'}`}>
                                                <span>
                                                    {t("settlements.minAmountLabel", {
                                                        amount: formatRial(MIN_SETTLEMENT_AMOUNT),
                                                    })}
                                                </span>
                                                <span className="mx-1">|</span>
                                                <span>
                                                    {t("settlements.maxAmountLabel", {
                                                        amount: formatRial(walletBalance),
                                                    })}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mb-4 sm:mb-5">
                                            <label
                                                className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-colors ${confirmed
                                                    ? "border-teal-300 bg-teal-50 dark:!border-teal-700 dark:!bg-teal-900/20"
                                                    : "border-gray-200 dark:!border-zinc-700 hover:bg-gray-50 dark:hover:!bg-zinc-700/30"
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={confirmed}
                                                    onChange={(e) => {
                                                        setConfirmed(e.target.checked);
                                                        setError("");
                                                    }}
                                                    className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 shrink-0"
                                                />
                                                <span className="text-xs sm:text-sm text-gray-700 dark:!text-gray-300">
                                                    {t("settlements.confirmCheckbox")}
                                                </span>
                                            </label>
                                        </div>

                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting || !isFormValid}
                                            className={`w-full py-2.5 sm:py-3 px-4 rounded-lg font-medium text-white transition-colors text-sm sm:text-base ${submitting || !isFormValid
                                                ? "bg-gray-400 dark:!bg-slate-700 cursor-not-allowed"
                                                : "bg-teal-600 hover:bg-teal-700 active:bg-teal-800"
                                                }`}
                                        >
                                            {submitting ? (
                                                <div className="flex items-center justify-center">
                                                    <svg
                                                        className="animate-spin -ml-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        />
                                                    </svg>
                                                    {t("settlements.submitting")}
                                                </div>
                                            ) : (
                                                t("settlements.submit")
                                            )}
                                        </button>
                                    </div>

                                    {/* ================ GUIDE CARD ================ */}
                                    <div className="bg-white dark:bg-gradient-to-br dark:!from-zinc-950 dark:!via-slate-900 dark:!to-teal-950 rounded-xl shadow-sm border border-gray-200 dark:!border-zinc-700 p-4 sm:p-6">
                                        <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:!text-white mb-4">
                                            {t("settlements.guideTitle")}
                                        </h2>
                                        <div className="space-y-5 sm:space-y-6">
                                            {[
                                                t("settlements.guideItems.iban"),
                                                t("settlements.guideItems.amount"),
                                                t("settlements.guideItems.card"),
                                                t("settlements.guideItems.reviewTime"),
                                                t("settlements.guideItems.payout"),
                                            ].map((text, index) => (
                                                <div key={index} className="flex items-start gap-2.5 sm:gap-3">
                                                    <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-teal-100 dark:!bg-teal-900/30 rounded-full flex items-center justify-center">
                                                        <span className={`text-teal-600 dark:!text-teal-400 font-bold text-xs sm:text-sm ${isEnglish && 'font-sans'}`}>
                                                            {toDisplayDigits(index + 1, isEnglish)}
                                                        </span>
                                                    </div>
                                                    <p className="text-gray-700 dark:!text-gray-300 text-xs sm:text-sm pt-0.5 sm:pt-1">
                                                        {text}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* ================ HISTORY ================ */}
                                <div className="mt-4 sm:mt-8 bg-white dark:bg-gradient-to-br dark:!from-zinc-950 dark:!via-slate-900 dark:!to-teal-950 rounded-xl shadow-sm border border-gray-200 dark:!border-zinc-700 p-4 sm:p-6">
                                    <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:!text-white mb-4 sm:mb-6">
                                        {t("settlements.historyTitle")}
                                        <span className="mr-2 text-xs sm:text-sm font-normal text-gray-500 dark:!text-gray-400">
                                            {t("settlements.requestCount", { count: requests.length })}
                                        </span>
                                    </h2>

                                    {loadingRequests ? (
                                        <div className="flex justify-center py-10">
                                            <ProgressBar
                                                visible={true}
                                                height="50"
                                                width="50"
                                                barColor="#14B8A6"
                                                borderColor="#FCD34D"
                                                ariaLabel="loading"
                                            />
                                        </div>
                                    ) : requests.length === 0 ? (
                                        <div className="text-center py-8 text-xs sm:text-sm text-gray-500 dark:!text-gray-400">
                                            {t("settlements.emptyHistory")}
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                                            <table className="w-full min-w-[720px]">
                                                <thead className="bg-gray-50 dark:!bg-zinc-700/50">
                                                    <tr>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.type")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.date")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.amount")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.iban")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.cardNumber")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.status")}
                                                        </th>
                                                        <th className="px-3 py-2.5 sm:px-4 sm:py-3 text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:!text-gray-400 whitespace-nowrap">
                                                            {t("settlements.table.receipt")}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 dark:!divide-zinc-700">
                                                    {requests.map((request) => {
                                                        const status = getStatusBadge(request.status);
                                                        return (
                                                            <tr
                                                                key={request.id}
                                                                className="text-center hover:bg-gray-50 dark:hover:!bg-zinc-700/30 transition-colors"
                                                            >
                                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm text-gray-700 dark:!text-gray-300 whitespace-nowrap">
                                                                    {request.type === "manual"
                                                                        ? t("settlements.types.manual")
                                                                        : t("settlements.types.automatic")}
                                                                </td>
                                                                <td className={`px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm text-gray-700 dark:!text-gray-300 tabular-nums whitespace-nowrap ${isEnglish && 'font-sans'}`}>
                                                                    {formatDate(request.date)}
                                                                </td>
                                                                <td className={`px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-bold text-amber-600 dark:!text-amber-300 tabular-nums whitespace-nowrap ${isEnglish && 'font-sans'}`}>
                                                                    {(request.amount).toLocaleString('en-US')}
                                                                </td>
                                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-mono text-gray-700 dark:!text-gray-300 tabular-nums whitespace-nowrap" dir="ltr">
                                                                    {request.iban}
                                                                </td>
                                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 text-xs sm:text-sm font-mono text-gray-700 dark:!text-gray-300 tabular-nums whitespace-nowrap" dir="ltr">
                                                                    {request.cardNumber || "—"}
                                                                </td>
                                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 whitespace-nowrap">
                                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] sm:text-xs font-bold ${status.color}`}>
                                                                        {status.icon}
                                                                        <span>{status.text}</span>
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 py-2.5 sm:px-4 sm:py-3 whitespace-nowrap">
                                                                    {request.receiptImageUrl ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleShowReceipt(request)}
                                                                            className="inline-flex items-center justify-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-lg bg-teal-500/10 text-teal-700 dark:!text-teal-300 border border-teal-500/30 hover:bg-teal-500/20 transition-colors text-[10px] sm:text-xs font-bold"
                                                                            title={t("settlements.receipt.showTitle")}
                                                                        >
                                                                            <FaEye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                                                            <span>{t("settlements.receipt.show")}</span>
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-[10px] sm:text-xs text-gray-400 dark:!text-gray-500">
                                                                            {t("settlements.receipt.noReceipt")}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* ================ RECEIPT MODAL ================ */}
            {selectedReceipt && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 dark:!bg-black/70 backdrop-blur-sm p-4"
                    onClick={closeReceiptModal}
                >
                    <div
                        className="relative bg-white dark:!bg-zinc-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-teal-500/20 dark:!border-teal-400/20"
                        dir="rtl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 dark:!border-zinc-700">
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-teal-500/10 dark:!bg-teal-900/30 border border-teal-500/20 flex items-center justify-center shrink-0">
                                    <FaFileImage className="text-teal-600 dark:!text-teal-400 text-sm" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:!text-white">
                                        {t("settlements.receipt.modalTitle")}
                                    </h3>
                                    <p className="text-[10px] sm:text-xs text-gray-500 dark:!text-gray-400 mt-0.5 truncate">
                                        {t("settlements.receipt.modalAmount")}{" "}
                                        <span className={`font-bold text-amber-600 dark:!text-amber-300 tabular-nums ${isEnglish && 'font-sans'}`}>
                                            {(selectedReceipt.request.amount).toLocaleString('en-US')}
                                        </span>{" "}
                                        {t("settlements.currency")}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeReceiptModal}
                                className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-500/10 dark:!hover:bg-zinc-700 transition-colors shrink-0"
                            >
                                <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                        </div>

                        <div className="p-3 sm:p-5 bg-gray-100 dark:!bg-zinc-900 flex items-center justify-center max-h-[70vh] overflow-auto">
                            <img
                                src={selectedReceipt.url}
                                alt={t("settlements.receipt.alt")}
                                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-md"
                                onError={(e) => {
                                    console.error("Receipt image load error:", selectedReceipt.url);
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        </div>

                        <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-gray-200 dark:!border-zinc-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                            <div className="text-[10px] sm:text-xs font-mono text-gray-700 dark:!text-gray-400 break-all order-2 sm:order-1">
                                {selectedReceipt.request.reviewNote}
                            </div>
                            <button
                                type="button"
                                onClick={closeReceiptModal}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:!bg-zinc-700 dark:hover:!bg-zinc-600 text-gray-700 dark:!text-white rounded-lg text-xs sm:text-sm font-medium transition-colors order-1 sm:order-2 shrink-0"
                            >
                                {t("settlements.receipt.close")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Settlements = () => (
    <SidebarProvider>
        <SettlementsInner />
    </SidebarProvider>
);

export default Settlements;